/**
 * DeleteTemplateDialog Component (Story 5.14, Task 6)
 *
 * Confirmation dialog for deleting a custom template.
 * Only shown for custom templates (isSystemTemplate === 0).
 */

import { Trash } from "@phosphor-icons/react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@provenleap/ui";
import { useTemplateMutations } from "../../hooks/useTemplateMutations";

interface DeleteTemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId: string;
  templateName: string;
  onSuccess?: () => void;
}

export function DeleteTemplateDialog({
  open,
  onOpenChange,
  templateId,
  templateName,
  onSuccess,
}: DeleteTemplateDialogProps) {
  const { deleteTemplate } = useTemplateMutations();

  const handleDelete = () => {
    if (deleteTemplate.isPending) return;

    deleteTemplate.mutate(templateId, {
      onSuccess: () => {
        onOpenChange(false);
        onSuccess?.();
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="delete-template-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash size={20} />
            Delete Template
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to delete{" "}
            <strong className="text-foreground">{templateName}</strong>? This
            action cannot be undone. Existing reports generated from this
            template will not be affected.
          </DialogDescription>
        </DialogHeader>

        {deleteTemplate.error && (
          <p className="text-sm text-red-500 bg-red-50 rounded-md px-3 py-2">
            {deleteTemplate.error.message}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={deleteTemplate.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={deleteTemplate.isPending}
            data-testid="delete-template-confirm"
          >
            {deleteTemplate.isPending ? "Deleting..." : "Delete Template"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
